import React, { useState, useEffect } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Edit2, Trash2, MapPin, X, Check, Building } from 'lucide-react';

const PRACTICE_TYPES = [
  { value: 'hospital', label: 'Hospital' },
  { value: 'private_clinic', label: 'Private Clinic' },
  { value: 'medical_center', label: 'Medical Center' },
  { value: 'home_practice', label: 'Home Practice' },
];

const emptyClinic = {
  clinic_name: '', practice_type: 'private_clinic', address: '', country: 'India', state: '', city: '', pin_code: '', landmark: '',
  clinic_phone: '', website: '', google_maps_link: '', is_current: true,
  consultation_fee: '', home_visit_fee: '',
  consultation_duration: 15, lead_time: 30, max_patients_per_day: 30, buffer_time: 5,
};

export default function Step4Practice({ data = [], onSave }) {
  const [entries, setEntries] = useState(Array.isArray(data) ? data : []);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(entries.length === 0);
  const [form, setForm] = useState(emptyClinic);

  useEffect(() => { setEntries(Array.isArray(data) ? data : []); }, [data]);
  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleAdd = () => {
    if (!form.clinic_name || !form.city) return;
    let newEntries = editing !== null ? entries.map((e, i) => i === editing ? form : e) : [...entries, form];
    if (form.is_current) {
      const targetIdx = editing !== null ? editing : newEntries.length - 1;
      newEntries = newEntries.map((e, i) => ({ ...e, is_current: i === targetIdx }));
    }
    setEntries(newEntries);
    setForm(emptyClinic);
    setEditing(null);
    setShowForm(false);
  };
  const handleEdit = (i) => { setForm(entries[i]); setEditing(i); setShowForm(true); };
  const handleDelete = (i) => setEntries(entries.filter((_, idx) => idx !== i));
  const handleCancel = () => { setForm(emptyClinic); setEditing(null); setShowForm(false); };

  return (
    <form id="step-form-4" onSubmit={(e) => { e.preventDefault(); onSave(entries); }} className="p-6 lg:p-8 space-y-5">
      {entries.length > 0 && (
        <div className="space-y-3">
          {entries.map((clinic, i) => (
            <div key={i} className="p-4 rounded-xl border border-slate-200 bg-slate-50 flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#3B1C73]/10 flex items-center justify-center flex-shrink-0"><Building className="w-5 h-5 text-[#3B1C73]" /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-slate-900">{clinic.clinic_name}</p>
                  {clinic.is_current && <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Current</span>}
                  <span className="text-xs bg-slate-200 text-slate-700 px-2 py-0.5 rounded-full capitalize">{clinic.practice_type?.replace(/_/g, ' ')}</span>
                </div>
                <p className="text-sm text-slate-600 mt-1">{clinic.address}</p>
                <p className="text-xs text-slate-500 mt-1">{clinic.city}{clinic.state ? `, ${clinic.state}` : ''} · {clinic.pin_code}</p>
                <div className="flex gap-3 mt-2 text-xs text-slate-600 flex-wrap">
                  {clinic.consultation_fee && <span>💰 ₹{clinic.consultation_fee} consultation</span>}
                  {clinic.home_visit_fee && <span>🏠 ₹{clinic.home_visit_fee} home visit</span>}
                </div>
              </div>
              <div className="flex gap-1">
                <button type="button" onClick={() => handleEdit(i)} className="p-2 text-slate-500 hover:text-[#3B1C73] hover:bg-white rounded-lg" data-testid={`clinic-edit-${i}`}><Edit2 className="w-4 h-4" /></button>
                <button type="button" onClick={() => handleDelete(i)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg" data-testid={`clinic-delete-${i}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm ? (
        <div className="p-5 rounded-xl border-2 border-[#3B1C73]/20 bg-purple-50/30 space-y-5">
          <div className="flex justify-between items-center">
            <h4 className="font-semibold text-slate-900">{editing !== null ? 'Edit Clinic' : 'Add Clinic / Practice Location'}</h4>
            <button type="button" onClick={handleCancel} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Clinic Name *</Label><Input required value={form.clinic_name} onChange={(e) => update('clinic_name', e.target.value)} placeholder="e.g. Gagoria Dental Clinic" className="mt-1.5" data-testid="clinic-name" /></div>
            <div>
              <Label>Practice Type *</Label>
              <Select value={form.practice_type} onValueChange={(v) => update('practice_type', v)}>
                <SelectTrigger className="mt-1.5" data-testid="clinic-type"><SelectValue /></SelectTrigger>
                <SelectContent>{PRACTICE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div><Label>Full Address *</Label><Textarea rows={2} value={form.address} onChange={(e) => update('address', e.target.value)} placeholder="Building, Street, Area" className="mt-1.5" data-testid="clinic-address" /></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div><Label>Country</Label><Input value={form.country} disabled className="mt-1.5 bg-slate-100" /></div>
            <div><Label>State</Label><Input value={form.state} onChange={(e) => update('state', e.target.value)} placeholder="Haryana" className="mt-1.5" /></div>
            <div><Label>City *</Label><Input required value={form.city} onChange={(e) => update('city', e.target.value)} placeholder="Gurugram" className="mt-1.5" data-testid="clinic-city" /></div>
            <div><Label>PIN Code</Label><Input maxLength={6} value={form.pin_code} onChange={(e) => update('pin_code', e.target.value.replace(/\D/g, ''))} placeholder="122001" className="mt-1.5" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Landmark</Label><Input value={form.landmark} onChange={(e) => update('landmark', e.target.value)} placeholder="Near Metro Station" className="mt-1.5" /></div>
            <div><Label>Google Maps Link</Label><Input value={form.google_maps_link} onChange={(e) => update('google_maps_link', e.target.value)} placeholder="https://maps.google.com/..." className="mt-1.5" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Clinic Phone</Label><Input type="tel" value={form.clinic_phone} onChange={(e) => update('clinic_phone', e.target.value)} placeholder="+91 9876543210" className="mt-1.5" /></div>
            <div><Label>Website</Label><Input value={form.website} onChange={(e) => update('website', e.target.value)} placeholder="https://clinic.com" className="mt-1.5" /></div>
          </div>

          <div className="pt-3 border-t border-slate-200">
            <h5 className="font-semibold text-slate-700 mb-3 text-sm">💰 Consultation Fees at This Location</h5>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Clinic Consultation Fee (₹)</Label><Input type="number" min={0} value={form.consultation_fee} onChange={(e) => update('consultation_fee', e.target.value)} placeholder="500" className="mt-1.5" data-testid="clinic-fee" /></div>
              <div><Label>Home Visit Fee (₹)</Label><Input type="number" min={0} value={form.home_visit_fee} onChange={(e) => update('home_visit_fee', e.target.value)} placeholder="1000" className="mt-1.5" /></div>
            </div>
          </div>

          <div className="pt-3 border-t border-slate-200">
            <h5 className="font-semibold text-slate-700 mb-3 text-sm">⏱ Booking Configuration</h5>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div><Label>Duration (min)</Label><Input type="number" min={5} value={form.consultation_duration} onChange={(e) => update('consultation_duration', parseInt(e.target.value) || 15)} className="mt-1.5" /></div>
              <div><Label>Lead Time (min)</Label><Input type="number" min={0} value={form.lead_time} onChange={(e) => update('lead_time', parseInt(e.target.value) || 0)} className="mt-1.5" /></div>
              <div><Label>Max Patients/Day</Label><Input type="number" min={1} value={form.max_patients_per_day} onChange={(e) => update('max_patients_per_day', parseInt(e.target.value) || 30)} className="mt-1.5" /></div>
              <div><Label>Buffer Time (min)</Label><Input type="number" min={0} value={form.buffer_time} onChange={(e) => update('buffer_time', parseInt(e.target.value) || 0)} className="mt-1.5" /></div>
            </div>
          </div>

          <label className="flex items-center gap-2 p-3 bg-white rounded-lg border border-slate-200 cursor-pointer">
            <input type="checkbox" checked={form.is_current} onChange={(e) => update('is_current', e.target.checked)} className="w-5 h-5 accent-[#3B1C73]" />
            <span className="text-sm font-medium text-slate-700">This is my currently active practice</span>
          </label>

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
            <button type="button" onClick={handleCancel} className="px-4 py-2 text-slate-600 hover:bg-white rounded-lg">Cancel</button>
            <button type="button" onClick={handleAdd} className="px-4 py-2 bg-[#3B1C73] text-white rounded-lg hover:bg-[#2c1457] flex items-center gap-1 text-sm font-medium" data-testid="clinic-save"><Check className="w-4 h-4" /> {editing !== null ? 'Update Clinic' : 'Add Clinic'}</button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => setShowForm(true)} className="w-full py-4 border-2 border-dashed border-slate-300 hover:border-[#3B1C73] hover:bg-[#3B1C73]/5 rounded-xl text-slate-600 hover:text-[#3B1C73] font-medium flex items-center justify-center gap-2" data-testid="clinic-add-btn">
          <Plus className="w-5 h-5" /> Add {entries.length > 0 ? 'Another' : 'Your'} Clinic
        </button>
      )}

      {entries.length === 0 && !showForm && (
        <div className="text-center text-slate-500 text-sm p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <MapPin className="w-5 h-5 mx-auto mb-1 text-amber-600" />
          Add at least one clinic to receive patient bookings
        </div>
      )}
    </form>
  );
}
